import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DEMO_PRESETS, clearAllData } from "@/lib/demoData";
import { Trash2, FlaskConical } from "lucide-react";

interface DemoToolsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DemoTools({ open, onOpenChange }: DemoToolsProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [clearFirst, setClearFirst] = useState(true);

  const handleLoad = async (preset: typeof DEMO_PRESETS[number]) => {
    setLoading(preset.id);
    if (clearFirst) clearAllData();
    await preset.load();
    setTimeout(() => {
      setLoading(null);
      onOpenChange(false);
      window.location.href = "/";
    }, 400);
  };

  const handleClearOnly = () => {
    clearAllData();
    onOpenChange(false);
    window.location.href = "/";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-2xl border-0 p-0 overflow-hidden" style={{
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
        border: "1px solid rgba(255,255,255,0.15)",
      }}>
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="flex items-center gap-2 text-white text-lg" style={{ fontFamily: "'Audiowide', sans-serif" }}>
            <FlaskConical className="w-5 h-5 text-emerald-400" />
            DEMO TOOLS
          </DialogTitle>
          <p className="text-xs text-white/50 mt-1">Dev mode only · PIN is always 1234</p>
        </DialogHeader>

        <div className="p-5 space-y-3">
          {/* Clear first toggle */}
          <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={clearFirst}
              onChange={e => setClearFirst(e.target.checked)}
              className="rounded"
            />
            Clear existing data before loading
          </label>

          {/* Preset buttons */}
          {DEMO_PRESETS.map(preset => (
            <button
              key={preset.id}
              onClick={() => handleLoad(preset)}
              disabled={loading !== null}
              className="w-full text-left rounded-xl p-4 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              style={{
                background: loading === preset.id ? "rgba(16, 185, 129, 0.2)" : "rgba(255,255,255,0.06)",
                border: loading === preset.id ? "1px solid rgba(16, 185, 129, 0.5)" : "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{preset.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-white">{preset.label}</p>
                  <p className="text-xs text-white/50 mt-0.5">{preset.description}</p>
                </div>
                {loading === preset.id && (
                  <div className="w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
            </button>
          ))}

          {/* Clear only */}
          <button
            onClick={handleClearOnly}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-bold text-red-400 transition-all hover:bg-red-500/10"
            style={{ border: "1px solid rgba(239, 68, 68, 0.2)" }}
          >
            <Trash2 className="w-3.5 h-3.5" />
            CLEAR ALL DATA ONLY
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
